/* =============================================================
   AuthContext.js — Authentication state management
   -------------------------------------------------------------
   Provides login / logout / sendMagicLink / refreshUser and the
   current user object to every component via React Context.

   User object shape (available after profile loads):
     { id, email, role, name, title, university, universityId, initials, avatarBg }

   TWO-PHASE ARCHITECTURE:
     Phase 1 — Session check (< 100 ms, reads localStorage, no network)
       onAuthStateChange fires INITIAL_SESSION immediately.
       `loading` goes false as soon as this event fires.
       ProtectedRoute unblocks here — the user is confirmed in or out.

     Phase 2 — Profile fetch (network call, runs in background)
       fetchProfile() runs after session is confirmed.
       `user` goes from null → full object when it completes.
       If the fetch is slow (slow network, tab was frozen, etc.) the
       app keeps the user inside — it never redirects to /login.
       Components that need profile fields show a brief spinner via
       the `!user` check in ProtectedRoute.

   WHY THIS IS STABLE:
     `authUser` (the session object) lives in React state in memory.
     It survives tab switches, Chrome tab freezes, and slow networks
     because it comes from localStorage — no network required.
     Profile fetch failures or timeouts don't affect routing at all.

   LOGOUT — fire-and-forget signOut():
     signOut() is not awaited. Awaiting it while the user tries to
     log back in causes GoTrueClient to queue signInWithPassword()
     behind the in-flight signOut(), hanging login indefinitely.
   ============================================================= */

import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'


/* ─── CONTEXT ────────────────────────────────────────────────── */
const AuthContext = createContext(null)


/* ─── fetchProfile ───────────────────────────────────────────── */
// Fetches profile + university for a confirmed Supabase auth user.
// Returns the full user object, or null on any error.
// Two separate queries to avoid RLS recursion on the profiles table.
async function fetchProfile(authUser) {
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('role, name, title, initials, avatar_bg, university_id')
    .eq('id', authUser.id)
    .single()

  if (error || !profile) return null

  let universityName = null
  if (profile.university_id) {
    const { data: uni } = await supabase
      .from('universities')
      .select('name')
      .eq('id', profile.university_id)
      .single()
    universityName = uni?.name ?? null
  }

  return {
    id:           authUser.id,
    email:        authUser.email,
    role:         profile.role,
    name:         profile.name,
    title:        profile.title      ?? null,
    university:   universityName,
    universityId: profile.university_id,
    initials:     profile.initials,
    avatarBg:     profile.avatar_bg,
  }
}


/* ─── AUTH PROVIDER ─────────────────────────────────────────── */
export function AuthProvider({ children }) {

  // undefined = session not yet checked (loading)
  // null      = checked, no active session
  // object    = active Supabase session user
  const [authUser, setAuthUser] = useState(undefined)

  // Full profile user (name, role, initials, etc.).
  // null while loading or when not authenticated.
  const [user, setUser] = useState(null)

  // Monotonically increasing counter — prevents a stale profile fetch
  // (e.g. from a previous login) from overwriting the current user.
  const fetchGenRef = useRef(0)

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const nextAuthUser = session?.user ?? null

        // Update session state immediately — this is what controls routing.
        // No network call needed here; Supabase reads from localStorage.
        setAuthUser(nextAuthUser)

        if (nextAuthUser) {
          // Fetch profile in the background — doesn't block routing.
          // Only re-fetch on events that can actually change identity.
          const needsProfileFetch =
            event === 'INITIAL_SESSION' ||
            event === 'SIGNED_IN'       ||
            event === 'USER_UPDATED'

          if (needsProfileFetch) {
            const gen = ++fetchGenRef.current
            fetchProfile(nextAuthUser).then(profile => {
              // Discard if a newer fetch has been started (e.g. rapid login/logout).
              if (fetchGenRef.current === gen) setUser(profile)
            })
          }
        } else {
          // No session — clear profile and invalidate any in-flight fetch.
          fetchGenRef.current++
          setUser(null)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])


  /* ─── login ──────────────────────────────────────────────── */
  // Throws on failure so Login.jsx can display the error message.
  // On success: onAuthStateChange fires SIGNED_IN → fetchProfile
  // → setUser → LoginRoute redirects to the correct portal.
  const login = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({
      email:    email.trim().toLowerCase(),
      password,
    })
    if (error) throw error
  }


  /* ─── sendMagicLink ──────────────────────────────────────── */
  const sendMagicLink = async (email) => {
    const { error } = await supabase.auth.signInWithOtp({
      email:   email.trim().toLowerCase(),
      options: { shouldCreateUser: false },
    })
    if (error) throw error
  }


  /* ─── logout ─────────────────────────────────────────────── */
  // signOut() is intentionally not awaited — see file header.
  const logout = () => {
    fetchGenRef.current++
    setUser(null)
    setAuthUser(null)
    supabase.auth.signOut()
  }


  /* ─── refreshUser ────────────────────────────────────────── */
  // Re-fetches profile + university and updates the user object.
  // Call this after updating profiles or universities in Supabase.
  const refreshUser = async () => {
    const { data: { user: au } } = await supabase.auth.getUser()
    if (!au) return
    const gen = ++fetchGenRef.current
    fetchProfile(au).then(profile => {
      if (fetchGenRef.current === gen) setUser(profile)
    })
  }


  // loading = true only for the brief moment before the first
  // onAuthStateChange event tells us whether a session exists.
  const loading = authUser === undefined

  return (
    <AuthContext.Provider value={{ user, loading, authUser, login, sendMagicLink, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}


/* ─── useAuth HOOK ───────────────────────────────────────────── */
export const useAuth = () => useContext(AuthContext)
